import { debounce } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
// types and utils
import cx from 'classnames'
import { CallsConstantsEnum } from 'features/calls/types'
import { focus, scrollElementToBottom } from 'utils'
// state
import { updateCallNotesApi } from 'features/calls/callsApi'
import { useCall } from 'features/calls/callsHooks'
import { AxiosError } from 'axios'
import { errorHandler } from 'utils/errors/errors'
import { ErrorMessagesEnum } from 'types/errorTypes'

interface IProps {
  disabled?: boolean
  callId: string
}

const CallNotesForm = ({ disabled, callId }: IProps) => {
  const { data: call } = useCall(callId, {
    onError: (err: AxiosError) => {
      errorHandler.handleError(err, ErrorMessagesEnum.CallError)
    },
  })
  // local state
  const [notes, setNotes] = useState<string>(``)

  const initialState = { saved: false, loading: false }
  const [state, setState] = useState(initialState)
  const [error, setError] = useState<boolean>(false)

  useEffect(() => {
    call?.notes && setNotes(call.notes)
  }, [call?.notes])

  useEffect(() => {
    scrollElementToBottom('add-note')
  })

  useEffect(() => {
    return () => {
      handleChangeDebounce.cancel()
    }
  }, [])

  // handlers
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value !== notes) {
      setNotes(e.target.value)
      if (error) setError(false)

      // Update local state
      setState((prev) => ({
        ...prev,
        saved: false,
        loading: false,
      }))

      handleChangeDebounce(e.target.value)
    }
  }
  const handleSave = async (notes: string) => {
    // Set loading true
    setState((prev) => ({
      ...prev,
      saved: false,
      loading: true,
    }))
    if (error) setError(false)

    try {
      call?.jobId && (await updateCallNotesApi(call.jobId, notes))

      setState((prev) => ({
        ...prev,
        saved: true,
        loading: false,
      }))

      // Focus textarea element
      focus('add-note')
    } catch (error: any) {
      errorHandler.handleError(error, ErrorMessagesEnum.CallNotes)
      setError(true)
    } finally {
      // set the state back after delay
      setTimeout(() => setState(initialState), 500)
    }
  }

  const handleChangeDebounce = useCallback(
    debounce((notes) => handleSave(notes), CallsConstantsEnum.timerTimeout),
    [],
  )
  return (
    <Div className={cx({ inactive: disabled })}>
      <div className="label">
        {state.saved && 'Saved'}
        {state.loading && 'Saving...'}
      </div>

      <textarea
        className="form-input grey-scrollbar"
        name="add-note"
        id="add-note"
        value={notes}
        onChange={handleChange}
        placeholder="Click here to type notes"
        readOnly={disabled}
        data-testid="callNotesTextarea"
      ></textarea>
    </Div>
  )
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 0 0.15rem ${({ theme }) => theme.colors.grey.light};
  border-radius: 0.5rem;

  > textarea {
    color: ${({ theme }) => theme.colors.grey.neutral};
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    border-radius: 0.5rem;
    border: none;
    padding: 1rem;
    flex: 1;
    width: 100%;
    resize: none;

    &:focus {
      outline: none;
    }

    &.hasError {
      box-shadow: ${({ theme }) => theme.colors.error.light};
    }
  }

  > .label {
    position: absolute;
    top: 0.5rem;
    right: 2.25rem;
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    ${({ theme }) => theme.typography.s.normal};
    font-style: italic;
    color: ${({ theme }) => theme.colors.primary.dark};
  }
`

export { CallNotesForm }
